// Site-wide 4-digit access gate (code: "0852").
//
// Waytide is not public-facing yet and the gate stays in place until the
// Captain says otherwise. See memory/waytide_conventions.md — the gate must
// never be removed or no-opped without an explicit second confirmation.
//
// When the gate is passed it writes `wt_gate_passed = GATE_CODE` to
// localStorage. When the GATE_CODE constant changes, every user is forced
// through again. While the gate is blocking, it purges auth keys so
// post-wipe stale tokens don't leak past a closed gate.

import { useState, useEffect } from "react";
import { ThemeSwitcher } from "../theme/ThemeSwitcher";
import { BrandLogo } from "../layout/Brand";

const GATE_CODE = "0852";
const STORAGE_KEY = "wt_gate_passed";

/** Auth keys to purge when the gate blocks — forces re-auth after DB wipe. */
const AUTH_KEYS = [
  "wt_token",
  "wt_identity",
  "wt_private_key",
  "wt_user",
  "wt_stdb_token",
];

export function useCodeGate() {
  // Store the code itself, not "1". Changing GATE_CODE forces everyone through again.
  const [passed, setPassed] = useState(
    () => localStorage.getItem(STORAGE_KEY) === GATE_CODE,
  );

  useEffect(() => {
    if (!passed) {
      for (const key of AUTH_KEYS) {
        localStorage.removeItem(key);
      }
    }
  }, [passed]);

  function unlock() {
    localStorage.setItem(STORAGE_KEY, GATE_CODE);
    setPassed(true);
  }

  return { passed, unlock };
}

export function CodeGatePage({ onUnlock }: { onUnlock: () => void }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  function tryCode(value: string) {
    if (value === GATE_CODE) {
      onUnlock();
    } else {
      setError("Invalid code");
      setCode("");
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    setCode(value);
    setError("");
    if (value.length === 4) tryCode(value);
  }

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center bg-surface px-6 text-primary transition-colors">
      <div className="absolute right-4 top-4">
        <ThemeSwitcher />
      </div>

      <div className="mb-16 flex cursor-default select-none flex-col items-center gap-3 text-accent">
        <BrandLogo size={56} />
        <h1 className="text-center text-5xl font-semibold leading-none tracking-[0.02em] text-primary">
          Waytide
        </h1>
      </div>

      <div className="w-full max-w-sm rounded-panel border border-edge bg-surface-glass p-8 shadow-[var(--wt-shadow)] backdrop-blur">
        <div className="flex flex-col gap-6">
          <p className="text-center text-sm text-muted">
            Enter access code to continue
          </p>

          <input
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={code}
            onChange={handleChange}
            placeholder="0000"
            required
            autoFocus
            // tracking-[0.5em] here is typography for digit chunking
            // (the eye reads spaced digits faster), not status-label
            // wide-tracking. Coral has blessed this site; do not lift
            // to another UI surface without re-checking.
            className="rounded-md border border-edge bg-surface-raised px-4 py-3 text-center text-2xl font-bold tracking-[0.5em] text-primary outline-none transition-colors duration-200 placeholder:text-faint focus:border-accent"
          />
        </div>

        {error && (
          <p className="mt-4 text-center text-xs text-danger">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
